import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence, useInView, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useAppContext } from '../context/AppContext';
import { useAppStoreData } from '../hooks/useAppStoreData';
import { groupProjectsByCompany, onLaunchRequest } from '../utils/simulator';
import { Container, SectionShell, alpha, ease, fonts, hover, pressable, radius, text } from '../styles/GlobalStyles';
import SectionHeader from './common/SectionHeader';
import Reveal from './common/Reveal';
import Device from './simulator/Device';
import StatusBar from './simulator/StatusBar';
import DynamicIsland, { IslandState } from './simulator/DynamicIsland';
import HomeScreen, { DockLink } from './simulator/HomeScreen';
import AppWindow, { LaunchOrigin } from './simulator/AppWindow';
import {
  CheckIcon,
  ChevronIcon,
  ExternalIcon,
  FolderIcon,
  HammerIcon,
  PhoneIcon,
  PlayIcon,
  StopIcon,
} from './common/Icons';

const DEVICE_NAME = 'iPhone 17 Pro';
const BUILD_MS = 1500;

type Phase = 'home' | 'building' | 'launching' | 'running';

interface LogLine {
  id: number;
  time: string;
  text: string;
  tone?: 'success' | 'muted' | 'accent';
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const timestamp = () => {
  const now = new Date();
  const pad = (value: number, size = 2) => String(value).padStart(size, '0');
  return `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}.${pad(now.getMilliseconds(), 3)}`;
};

// Position of the tapped icon relative to the screen, in %, so the app can zoom out of it.
const measureOrigin = (screen?: HTMLElement | null, icon?: HTMLElement | null): LaunchOrigin => {
  const screenRect = screen?.getBoundingClientRect();
  const iconRect = icon?.getBoundingClientRect();
  if (!screenRect || !iconRect || !screenRect.width) return { x: 42, y: 40, w: 16, h: 7.5, radius: 12 };
  return {
    x: ((iconRect.left - screenRect.left) / screenRect.width) * 100,
    y: ((iconRect.top - screenRect.top) / screenRect.height) * 100,
    w: (iconRect.width / screenRect.width) * 100,
    h: (iconRect.height / screenRect.height) * 100,
    radius: iconRect.width * 0.225,
  };
};

/* ---------- Layout ---------- */

// Transparent so the page's ambient background shows through.
const ProjectsSection = styled(SectionShell)`
  overflow: hidden;
`;

const Wide = styled(Container)`
  max-width: 1280px;
`;

const Perspective = styled.div`
  position: relative;
  perspective: 1800px;
`;

const IdeWindow = styled(motion.div)`
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors.borderStrong};
  background: ${props => props.theme.colors.cardBackground};
  box-shadow: ${props => props.theme.shadows.large};
  overflow: hidden;
  transform-origin: center top;
`;

/* ---------- Toolbar ---------- */

const Toolbar = styled.div`
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  height: 54px;
  padding: 0 14px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundAlt};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: auto minmax(0, 1fr);
    gap: 10px;
    height: auto;
    padding: 10px 12px;
  }
`;

const LeftTools = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const TrafficLights = styled.div`
  display: flex;
  gap: 8px;

  span {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    box-shadow: inset 0 0 0 0.5px rgba(0, 0, 0, 0.2);
  }

  span:nth-child(1) { background: #ff5f57; }
  span:nth-child(2) { background: #febc2e; }
  span:nth-child(3) { background: #28c840; }
`;

const ToolButton = styled.button`
  display: grid;
  place-items: center;
  width: 32px;
  height: 28px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  ${pressable}

  ${hover} {
    &:hover:not(:disabled) {
      background: ${props => props.theme.colors.border};
    }
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const Scheme = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: ${text.xs};
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  white-space: nowrap;

  svg {
    color: ${props => props.theme.colors.textTertiary};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const MiniIcon = styled.i<{ $src?: string; $size?: number }>`
  display: inline-block;
  flex-shrink: 0;
  width: ${props => props.$size ?? 18}px;
  height: ${props => props.$size ?? 18}px;
  border-radius: ${props => (props.$size ?? 18) * 0.24}px;
  background: ${props => (props.$src ? `#fff url(${props.$src}) center / cover no-repeat` : props.theme.colors.border)};
  box-shadow: 0 0 0 0.5px ${props => props.theme.colors.border};
`;

const Status = styled.div`
  position: relative;
  justify-self: center;
  width: 100%;
  max-width: 520px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 14px;
  border-radius: 8px;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  font-size: ${text.xs};
  color: ${props => props.theme.colors.textSecondary};
  overflow: hidden;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StatusProgress = styled(motion.div)`
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 100%;
  background: ${props => props.theme.colors.primary};
  transform-origin: left;
`;

const StatusIcon = styled.span<{ $tone: 'build' | 'run' | 'idle' }>`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: ${props =>
    props.$tone === 'run' ? props.theme.colors.success : props.$tone === 'build' ? props.theme.colors.primary : props.theme.colors.textTertiary};
`;

const DeviceBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${fonts.mono};
  font-size: 0.72rem;
  color: ${props => props.theme.colors.textTertiary};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

/* ---------- Body ---------- */

const Body = styled.div`
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr) 300px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Navigator = styled.aside`
  padding: 14px 10px;
  border-right: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundAlt};

  @media (max-width: 1100px) {
    display: none;
  }
`;

const PaneTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px 10px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.textTertiary};

  small {
    font-family: ${fonts.mono};
    font-weight: 500;
    letter-spacing: 0;
  }
`;

const GroupRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 8px 5px;
  font-size: ${text.xs};
  font-weight: 600;
  color: ${props => props.theme.colors.text};

  > svg:first-child {
    transform: rotate(90deg);
    color: ${props => props.theme.colors.textTertiary};
  }

  > svg:nth-child(2) {
    color: #5ac8fa;
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const NavItem = styled.button<{ $selected: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 6px 10px 6px 30px;
  border: none;
  border-radius: 7px;
  background: transparent;
  font-size: ${text.xs};
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  color: ${props => props.theme.colors.text};

  ${hover} {
    &:hover {
      background: ${props => (props.$selected ? 'transparent' : props.theme.colors.border)};
    }
  }

  > * {
    position: relative;
    z-index: 1;
  }

  span {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const NavSelection = styled(motion.div)`
  position: absolute !important;
  inset: 0;
  z-index: 0 !important;
  border-radius: 7px;
  background: ${props => alpha(props.theme.colors.primary, 16)};
`;

const RunningBadge = styled(motion.i)`
  display: grid;
  place-items: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${props => props.theme.colors.success};
  color: #fff;
  flex-shrink: 0;
`;

const Canvas = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: ${props => props.theme.colors.background};
`;

const MobileStrip = styled.div`
  display: none;
  gap: 14px;
  padding: 14px 16px 4px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 1100px) {
    display: flex;
  }
`;

const StripItem = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 64px;
  flex-shrink: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: ${text.xs};
  font-weight: 500;
  color: ${props => (props.$selected ? props.theme.colors.text : props.theme.colors.textSecondary)};

  ${MiniIcon} {
    transition: box-shadow 0.25s ease, transform 0.25s ease;
    ${props =>
      props.$selected &&
      css`
        box-shadow: 0 0 0 2px ${props.theme.colors.background}, 0 0 0 4px ${props.theme.colors.primary};
        transform: scale(1.05);
      `}
  }

  span:last-child {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const DeviceStage = styled.div`
  --device-w: clamp(250px, 21vw, 290px);
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 36px 24px 40px;
  overflow: hidden;

  @media (max-width: 1100px) {
    --device-w: min(72vw, 290px);
    padding: 24px 16px 32px;
  }
`;

const HomeIndicator = styled(motion.button)`
  position: absolute;
  left: 50%;
  bottom: 2.2cqw;
  width: 36cqw;
  height: 1.5cqw;
  margin-left: -18cqw;
  padding: 0;
  border: none;
  border-radius: 1cqw;
  background: rgba(255, 255, 255, 0.85);
  z-index: 46;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    inset: -3cqw -4cqw;
  }
`;

/* ---------- Inspector ---------- */

const Inspector = styled.aside`
  display: flex;
  flex-direction: column;
  border-left: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.cardBackground};
  min-width: 0;

  @media (max-width: 1100px) {
    border-left: none;
    border-top: 1px solid ${props => props.theme.colors.border};
  }
`;

const InspectorContent = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 20px;
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  h3 {
    font-size: ${text.base};
    font-weight: 700;
    letter-spacing: -0.02em;
    color: ${props => props.theme.colors.text};
  }

  p {
    margin-top: 2px;
    font-size: ${text.xs};
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const Properties = styled.dl`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  font-size: ${text.xs};
  border-top: 1px solid ${props => props.theme.colors.border};

  dt,
  dd {
    padding: 8px 0;
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }

  dt {
    padding-right: 14px;
    color: ${props => props.theme.colors.textTertiary};
  }

  dd {
    color: ${props => props.theme.colors.text};
    font-weight: 500;
    overflow-wrap: anywhere;
  }

  code {
    font-family: ${fonts.mono};
    font-size: 0.74rem;
  }
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const ActionButton = styled.a<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border-radius: ${radius.md};
  font-size: ${text.xs};
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${props => (props.$primary ? 'transparent' : props.theme.colors.borderStrong)};
  background: ${props => (props.$primary ? props.theme.colors.primary : 'transparent')};
  color: ${props => (props.$primary ? props.theme.colors.onPrimary : props.theme.colors.text)};
  ${pressable}

  ${hover} {
    &:hover {
      background: ${props => (props.$primary ? props.theme.colors.primaryHover : props.theme.colors.backgroundAlt)};
    }
  }
`;

const ConsolePanel = styled.div`
  margin-top: auto;
  border-top: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundAlt};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const ConsoleBody = styled.div`
  height: 150px;
  overflow-y: auto;
  padding: 4px 14px 12px;
  font-family: ${fonts.mono};
  font-size: 0.7rem;
  line-height: 1.7;
  scrollbar-width: thin;
`;

const LogRow = styled(motion.div)<{ $tone?: LogLine['tone'] }>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${props =>
    props.$tone === 'success'
      ? props.theme.colors.success
      : props.$tone === 'accent'
      ? props.theme.colors.primaryText
      : props.$tone === 'muted'
      ? props.theme.colors.textTertiary
      : props.theme.colors.text};

  time {
    color: ${props => props.theme.colors.textTertiary};
    margin-right: 8px;
  }
`;

const Hint = styled.p`
  margin-top: 20px;
  font-size: ${text.sm};
  color: ${props => props.theme.colors.textTertiary};
`;

/* ---------- Component ---------- */

const Projects: React.FC = () => {
  const { content, language } = useAppContext();
  const { items, simulator: labels } = content.projects;
  const ids = useMemo(() => items.map(item => item.appStoreId), [items]);
  const { apps } = useAppStoreData(ids);
  const groups = useMemo(() => groupProjectsByCompany(content), [content]);
  const reduceMotion = useReducedMotion();

  const perspectiveRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const screenRef = useRef<HTMLDivElement>(null);
  const consoleRef = useRef<HTMLDivElement>(null);
  const iconRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const inView = useInView(stageRef, { amount: 0.4 });

  const [booted, setBooted] = useState(false);
  const [phase, setPhase] = useState<Phase>('home');
  const [selectedId, setSelectedId] = useState(ids[0]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [origin, setOrigin] = useState<LaunchOrigin | null>(null);
  const [autoplay, setAutoplay] = useState(true);
  const [hovering, setHovering] = useState(false);
  const [buildKey, setBuildKey] = useState(0);
  const [logs, setLogs] = useState<LogLine[]>([]);

  // Refs mirror state for the async launch script, which outlives renders.
  const runRef = useRef(0);
  const activeRef = useRef<string | null>(null);
  const startedRef = useRef(false);
  const logIdRef = useRef(0);
  const appsRef = useRef(apps);
  const itemsRef = useRef(items);
  appsRef.current = apps;
  itemsRef.current = items;

  const { scrollYProgress } = useScroll({ target: perspectiveRef, offset: ['start end', 'start 0.2'] });
  const rotateX = useTransform(scrollYProgress, [0, 1], [8, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [0.97, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [0.3, 1]);

  const nameOf = useCallback((id: string) => itemsRef.current.find(item => item.appStoreId === id)?.name ?? id, []);

  const log = useCallback((text: string, tone?: LogLine['tone']) => {
    const line = { id: ++logIdRef.current, time: timestamp(), text, tone };
    setLogs(previous => [...previous.slice(-40), line]);
  }, []);

  const launch = useCallback(
    async (id: string, { build, user }: { build: boolean; user: boolean }) => {
      const run = ++runRef.current;
      const alive = () => runRef.current === run;
      const name = nameOf(id);
      startedRef.current = true;
      setBooted(true);
      setSelectedId(id);
      if (user) setAutoplay(false);

      if (activeRef.current) {
        log(`Message from debugger: ${nameOf(activeRef.current)} terminated`, 'muted');
        activeRef.current = null;
        setActiveId(null);
        setPhase('home');
        await sleep(520);
        if (!alive()) return;
      }

      if (build) {
        setPhase('building');
        setBuildKey(key => key + 1);
        log(`Build ${name} · Debug · ${DEVICE_NAME}`, 'accent');
        await sleep(BUILD_MS * 0.3);
        if (!alive()) return;
        log('Compiling Swift sources…', 'muted');
        await sleep(BUILD_MS * 0.35);
        if (!alive()) return;
        log(`Linking ${name}.app`, 'muted');
        await sleep(BUILD_MS * 0.35);
        if (!alive()) return;
        log('Build Succeeded', 'success');
      }

      setOrigin(measureOrigin(screenRef.current, iconRefs.current[id]));
      activeRef.current = id;
      setActiveId(id);
      setPhase('launching');
      log(`Launching ${appsRef.current[id]?.bundleId ?? name} on ${DEVICE_NAME}`);
      await sleep(1150);
      if (!alive()) return;
      setPhase('running');
    },
    [log, nameOf]
  );

  const goHome = useCallback(() => {
    runRef.current++;
    setAutoplay(false);
    if (activeRef.current) log(`Message from debugger: ${nameOf(activeRef.current)} terminated`, 'muted');
    activeRef.current = null;
    setActiveId(null);
    setPhase('home');
  }, [log, nameOf]);

  // Boot the phone the first time the simulator scrolls into view, then start the demo reel.
  useEffect(() => {
    if (inView && !booted) setBooted(true);
  }, [inView, booted]);

  useEffect(() => {
    if (!booted) return;
    const timer = setTimeout(() => {
      if (startedRef.current) return;
      log(`Booted ${DEVICE_NAME}`, 'muted');
      launch(ids[0], { build: true, user: false });
    }, 1700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booted]);

  // Other sections (e.g. Experience tags) can ask for an app to be launched.
  useEffect(
    () =>
      onLaunchRequest(id => {
        startedRef.current = true;
        setTimeout(() => launch(id, { build: true, user: true }), 650);
      }),
    [launch]
  );

  useEffect(() => {
    const body = consoleRef.current;
    if (body) body.scrollTop = body.scrollHeight;
  }, [logs]);

  const handleComplete = useCallback(() => {
    if (!autoplay || !activeRef.current) return false;
    const index = ids.indexOf(activeRef.current);
    launch(ids[(index + 1) % ids.length], { build: true, user: false });
    return true;
  }, [autoplay, ids, launch]);

  const handleScreenChange = useCallback(
    (index: number, total: number) => log(`▸ ${activeRef.current ? nameOf(activeRef.current) : ''} — screen ${index + 1}/${total}`, 'muted'),
    [log, nameOf]
  );

  const registerIcon = useCallback((id: string, element: HTMLDivElement | null) => {
    iconRefs.current[id] = element;
  }, []);

  const selected = items.find(item => item.appStoreId === selectedId) ?? items[0];
  const selectedInfo = apps[selected.appStoreId];
  const selectedGroup = groups.find(group => group.projects.includes(selected));
  const activeInfo = activeId ? apps[activeId] : undefined;
  const islandState: IslandState = phase === 'building' ? 'building' : activeId ? 'running' : 'idle';
  const buildingName = nameOf(selectedId);

  const dock: DockLink[] = [
    { kind: 'linkedin', href: content.personalInfo.linkedin, label: 'LinkedIn' },
    { kind: 'github', href: content.personalInfo.github, label: 'GitHub' },
    { kind: 'medium', href: content.personalInfo.medium, label: 'Medium' },
    { kind: 'mail', href: `mailto:${content.personalInfo.email}`, label: 'Mail' },
  ];

  const status =
    phase === 'building' ? (
      <>
        <StatusIcon $tone="build">
          <motion.span animate={{ rotate: [0, -25, 0] }} transition={{ repeat: Infinity, duration: 0.6 }} style={{ display: 'grid' }}>
            <HammerIcon size={13} />
          </motion.span>
        </StatusIcon>
        <span>
          {labels.building} {buildingName}…
        </span>
      </>
    ) : activeId ? (
      <>
        <StatusIcon $tone="run">
          <PlayIcon size={12} />
        </StatusIcon>
        <span>{labels.running.replace('{app}', nameOf(activeId)).replace('{device}', DEVICE_NAME)}</span>
      </>
    ) : (
      <>
        <StatusIcon $tone="idle">
          <CheckIcon size={13} />
        </StatusIcon>
        <span>{buildKey > 0 ? labels.buildSucceeded : labels.ready}</span>
      </>
    );

  return (
    <ProjectsSection id="projects">
      <Wide>
        <SectionHeader index={4} title={content.projects.title} subtitle={content.projects.subtitle} />

        <Perspective ref={perspectiveRef}>
          <IdeWindow style={reduceMotion ? undefined : { rotateX, scale, opacity }}>
            <Toolbar>
              <LeftTools>
                <TrafficLights>
                  <span />
                  <span />
                  <span />
                </TrafficLights>
                <ToolButton
                  onClick={() => launch(selectedId, { build: true, user: true })}
                  aria-label={labels.run}
                  title={labels.run}
                >
                  <PlayIcon size={15} />
                </ToolButton>
                <ToolButton
                  onClick={goHome}
                  disabled={!activeId && phase !== 'building'}
                  aria-label={labels.stop}
                  title={labels.stop}
                >
                  <StopIcon size={14} />
                </ToolButton>
              </LeftTools>

              <Scheme>
                <MiniIcon $src={selectedInfo?.icon} $size={16} />
                {selected.name}
                <ChevronIcon size={12} />
                <PhoneIcon size={14} />
                {DEVICE_NAME}
              </Scheme>

              <Status aria-live="polite">
                {status}
                {phase === 'building' && (
                  <StatusProgress
                    key={buildKey}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: BUILD_MS / 1000, ease: 'easeInOut' }}
                  />
                )}
              </Status>

              <DeviceBadge>
                <PhoneIcon size={13} />
                iOS · Simulator
              </DeviceBadge>
            </Toolbar>

            <Body>
              <Navigator>
                <PaneTitle>
                  {labels.navigator}
                  <small>{items.length}</small>
                </PaneTitle>
                {groups.map(group => (
                  <div key={group.company}>
                    <GroupRow>
                      <ChevronIcon size={11} />
                      <FolderIcon size={15} />
                      <span>{group.company}</span>
                    </GroupRow>
                    {group.projects.map(project => {
                      const id = project.appStoreId;
                      return (
                        <NavItem key={id} $selected={id === selectedId} onClick={() => launch(id, { build: true, user: true })}>
                          {id === selectedId && (
                            <NavSelection layoutId="navigator-selection" transition={{ type: 'spring', duration: 0.3, bounce: 0 }} />
                          )}
                          <MiniIcon $src={apps[id]?.icon} $size={20} />
                          <span>{project.name}</span>
                          <AnimatePresence>
                            {id === activeId && phase === 'running' && (
                              <RunningBadge initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
                                <PlayIcon size={8} />
                              </RunningBadge>
                            )}
                          </AnimatePresence>
                        </NavItem>
                      );
                    })}
                  </div>
                ))}
              </Navigator>

              <Canvas>
                <MobileStrip>
                  {items.map(project => (
                    <StripItem
                      key={project.appStoreId}
                      $selected={project.appStoreId === selectedId}
                      onClick={() => launch(project.appStoreId, { build: true, user: true })}
                    >
                      <MiniIcon $src={apps[project.appStoreId]?.icon} $size={52} />
                      <span>{project.name}</span>
                    </StripItem>
                  ))}
                </MobileStrip>

                <DeviceStage ref={stageRef} onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
                  <motion.div
                    initial={{ y: 16, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1, ease: ease.out }}
                  >
                    <Device width="var(--device-w)" screenRef={screenRef}>
                      <AnimatePresence>
                        {booted && (
                          <motion.div key="on" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
                            <HomeScreen
                              apps={items.map(item => ({ id: item.appStoreId, name: item.name, icon: apps[item.appStoreId]?.icon }))}
                              dock={dock}
                              booted={booted}
                              appOpen={!!activeId}
                              searchLabel={language === 'tr' ? 'Ara' : 'Search'}
                              onLaunch={id => launch(id, { build: false, user: true })}
                              registerIcon={registerIcon}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <AnimatePresence>
                        {activeId && origin && (
                          <AppWindow
                            key={activeId}
                            name={nameOf(activeId)}
                            icon={activeInfo?.icon}
                            screenshots={activeInfo?.screenshots ?? []}
                            origin={origin}
                            running={phase === 'running'}
                            paused={!inView || hovering}
                            onComplete={handleComplete}
                            onScreenChange={handleScreenChange}
                            onInteract={() => setAutoplay(false)}
                          />
                        )}
                      </AnimatePresence>

                      <StatusBar tone="light" />
                      <DynamicIsland
                        state={islandState}
                        icon={apps[phase === 'building' ? selectedId : activeId ?? '']?.icon}
                        title={buildingName}
                        subtitle={labels.building}
                        buildMs={BUILD_MS}
                      />

                      <AnimatePresence>
                        {activeId && (
                          <HomeIndicator
                            key="home-indicator"
                            onClick={goHome}
                            aria-label="Home"
                            title="Home"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          />
                        )}
                      </AnimatePresence>
                    </Device>
                  </motion.div>
                </DeviceStage>
              </Canvas>

              <Inspector>
                <AnimatePresence mode="wait">
                  <InspectorContent
                    key={selected.appStoreId}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18, ease: ease.out }}
                  >
                    <Identity>
                      <MiniIcon $src={selectedInfo?.icon} $size={64} />
                      <div>
                        <h3>{selected.name}</h3>
                        <p>{selected.description}</p>
                      </div>
                    </Identity>

                    <Properties>
                      <dt>{labels.category}</dt>
                      <dd>{selectedInfo?.genre ?? '—'}</dd>
                      <dt>{labels.publisher}</dt>
                      <dd>{selectedInfo?.seller ?? '—'}</dd>
                      {selectedGroup && selectedGroup.period && (
                        <>
                          <dt>{labels.builtAt}</dt>
                          <dd>{selectedGroup.company}</dd>
                        </>
                      )}
                      <dt>{labels.platform}</dt>
                      <dd>iOS · iPhone</dd>
                      <dt>Bundle ID</dt>
                      <dd>
                        <code>{selectedInfo?.bundleId ?? '—'}</code>
                      </dd>
                    </Properties>

                    <Actions>
                      <ActionButton
                        as="button"
                        $primary
                        onClick={() => launch(selected.appStoreId, { build: true, user: true })}
                      >
                        <PlayIcon size={12} />
                        {labels.run}
                      </ActionButton>
                      <ActionButton
                        href={selected.appStoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={labels.viewOnAppStore}
                      >
                        App Store
                        <ExternalIcon size={13} />
                      </ActionButton>
                    </Actions>
                  </InspectorContent>
                </AnimatePresence>

                <ConsolePanel>
                  <PaneTitle style={{ padding: '12px 14px 6px' }}>
                    {labels.console}
                    <small>{DEVICE_NAME}</small>
                  </PaneTitle>
                  <ConsoleBody ref={consoleRef}>
                    {logs.map(line => (
                      <LogRow key={line.id} $tone={line.tone} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }}>
                        <time>{line.time}</time>
                        {line.text}
                      </LogRow>
                    ))}
                  </ConsoleBody>
                </ConsolePanel>
              </Inspector>
            </Body>
          </IdeWindow>
        </Perspective>

        <Reveal delay={0.1}>
          <Hint>{labels.hint}</Hint>
        </Reveal>
      </Wide>
    </ProjectsSection>
  );
};

export default Projects;
